import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Flame, MapPin, Clock, Sparkle, Lightning } from "@phosphor-icons/react";
import { track as trackAnalytics } from "../lib/analytics";
import { FOOD_CATEGORIES } from "../lib/foodCategories";
import { fmtET } from "../lib/etTime";

const CAT_EMOJI = Object.fromEntries(
  FOOD_CATEGORIES.map((c) => [c.slug, c.emoji])
);

function formatTimeRange(startsAt, endsAt) {
  const s = fmtET(startsAt, { hour: "numeric", minute: "2-digit" });
  const e = fmtET(endsAt, { hour: "numeric", minute: "2-digit" });
  return s && e ? `${s} – ${e}` : "";
}

/**
 * Hotspot detail bottom-sheet. Mounted from MapView when the user taps
 * a 🔥 hotspot marker. Lists every truck participating in the gathering
 * (with logo / category emoji, name, Pro chip, boosted chip, time
 * window) and a single tap-handler that emits `hotspot_truck_click`
 * before deferring to the existing truck-detail flow.
 */
export default function HotspotSheet({ hotspot, onClose, onTruckTap }) {
  // Close on Escape — keyboard parity with the truck-detail sheet
  useEffect(() => {
    if (!hotspot) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [hotspot, onClose]);

  return (
    <AnimatePresence>
      {hotspot && (
        <motion.div
          key={hotspot.id}
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 280 }}
          className="fixed left-0 right-0 bottom-0 z-[1200] bg-card border-t border-border rounded-t-2xl shadow-2xl max-h-[75vh] flex flex-col"
          data-testid="hotspot-sheet"
        >
          {/* Header */}
          <div className="flex items-start gap-3 px-5 pt-5 pb-3 border-b border-border">
            <div className="h-12 w-12 rounded-full grid place-items-center bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/40 flex-none">
              <Flame weight="fill" className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-primary inline-flex items-center gap-1">
                <Sparkle weight="fill" className="h-3 w-3" />
                Food truck destination
              </div>
              <h2 className="font-display font-semibold text-lg leading-tight mt-0.5" data-testid="hotspot-sheet-title">
                {hotspot.count} trucks gathered here
              </h2>
              <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1.5 flex-wrap">
                {hotspot.starts_at && (
                  <>
                    <Clock weight="duotone" className="h-3.5 w-3.5" />
                    <span>{formatTimeRange(hotspot.starts_at, hotspot.ends_at)}</span>
                  </>
                )}
                <span className="inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full bg-primary/15 text-primary text-[10px] uppercase tracking-wider font-bold">
                  {hotspot.tier}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-full hover:bg-muted grid place-items-center flex-none"
              aria-label="Close hotspot detail"
              data-testid="hotspot-sheet-close"
            >
              <X weight="bold" className="h-4 w-4" />
            </button>
          </div>

          {/* Truck list */}
          <div className="overflow-y-auto px-3 py-2 flex-1" data-testid="hotspot-sheet-trucks">
            <ul className="divide-y divide-border">
              {(hotspot.trucks || []).map((t) => {
                const emoji = CAT_EMOJI[t.food_category] || "🚚";
                return (
                  <li key={t.id} data-testid={`hotspot-sheet-truck-${t.truck_id || t.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        trackAnalytics("hotspot_truck_click", hotspot.id);
                        onTruckTap?.(t);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-3 hover:bg-muted/50 rounded-lg text-left"
                    >
                      <div className="h-12 w-12 rounded-full bg-muted grid place-items-center flex-none overflow-hidden border border-border">
                        {t.logo_pin_url ? (
                          <img
                            src={t.logo_pin_url}
                            alt={`${t.truck_name} logo`}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-xl" aria-hidden>{emoji}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-semibold text-sm truncate">{t.truck_name}</span>
                          {t.owner_is_pro && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 h-4 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[9px] uppercase tracking-wider font-bold">
                              <Sparkle weight="fill" className="h-2.5 w-2.5" />
                              Pro
                            </span>
                          )}
                          {t.is_boosted && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 h-4 rounded-full bg-amber-400 text-amber-950 text-[9px] uppercase tracking-wider font-bold">
                              <Lightning weight="fill" className="h-2.5 w-2.5" />
                              Boosted
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                          <Clock weight="duotone" className="h-3 w-3" />
                          {formatTimeRange(t.starts_at, t.ends_at)}
                        </div>
                      </div>
                      {t.truck_id && (
                        <Link
                          to={`/t/${t.truck_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-[10px] uppercase tracking-wider font-bold text-primary px-2.5 h-7 rounded-full border border-primary/40 hover:bg-primary/10 grid place-items-center flex-none"
                          data-testid={`hotspot-sheet-profile-${t.truck_id}`}
                        >
                          View
                        </Link>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
            {hotspot.count > (hotspot.trucks || []).length && (
              <div className="text-center text-xs text-muted-foreground py-3">
                + {hotspot.count - hotspot.trucks.length} more truck{hotspot.count - hotspot.trucks.length === 1 ? "" : "s"} at this spot
              </div>
            )}
          </div>

          {/* Footer cue */}
          <div className="px-5 py-3 border-t border-border bg-muted/30 text-[11px] text-muted-foreground inline-flex items-center gap-1.5 justify-center">
            <MapPin weight="duotone" className="h-3.5 w-3.5" />
            Zoom in on the map to see individual trucks
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
